import {
  AGENT_UPDATE_CHANNEL_LABELS,
  type AgentRelease,
  type FeatureKey,
  type FeatureRollout,
} from "@station/contracts";
import {
  Alert,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Rocket } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReleaseRolloutDialog, RolloutDialog } from "../../features/platform/ops-dialogs.js";
import {
  useAgentDistribution,
  useAgentReleases,
  usePlatformPermissions,
  useRefreshPlatform,
  useRollouts,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { Section } from "../../features/platform/tenant-tabs.js";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../lib/format.js";

export const Route = createFileRoute("/platform/yayinlar")({ component: ReleasesRoute });

type DistributionRow = {
  channel: "stable" | "beta";
  version: string;
  os: string;
  arch: string;
  agents: number;
  online: number;
};

/**
 * Yayınlar (Prompt 24 web): özelliklerin kademeli açılışı (yüzde → kararlı kova; ya da kiracı listesi) ve istasyon
 * ajanlarının sürüm dağılımı / kademeli yükseltme yüzdesi. Yayın tamamlanınca plan tek başına karar verir.
 */
function ReleasesRoute(): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPlatform();
  const permissions = usePlatformPermissions();
  const canFeatures = permissions.has("platform.features.manage");
  const canReleases = permissions.has("platform.releases.manage");
  const rollouts = useRollouts();
  const distribution = useAgentDistribution();
  const releases = useAgentReleases();
  const [editing, setEditing] = useState<{ rollout: FeatureRollout | null } | null>(null);
  const [completing, setCompleting] = useState<FeatureRollout | null>(null);
  const [releaseEditing, setReleaseEditing] = useState<AgentRelease | null>(null);
  const complete = useMutation({
    mutationFn: async (featureKey: FeatureKey) =>
      (await api.DELETE("/platform/feature-rollouts/{featureKey}", { params: { path: { featureKey } } }))
        .data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("platform.rollouts.completed") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const rolloutColumns = useMemo<DataTableColumn<FeatureRollout>[]>(
    () => [
      {
        id: "feature",
        header: t("platform.fields.feature"),
        cell: (row) => <span className="font-medium">{row.label}</span>,
      },
      {
        id: "strategy",
        header: t("platform.fields.strategy"),
        cell: (row) =>
          row.strategy === "PERCENT"
            ? t("platform.rollouts.percentValue", { percent: row.percent })
            : t("platform.rollouts.listValue", { count: row.tenants.length }),
      },
      {
        id: "coverage",
        header: t("platform.fields.coverage"),
        cell: (row) =>
          t("platform.rollouts.coverage", { included: row.includedTenants, eligible: row.eligibleTenants }),
      },
      { id: "note", header: t("platform.fields.note"), cell: (row) => row.note ?? "—" },
      {
        id: "updated",
        header: t("platform.fields.updatedAt"),
        cell: (row) => <span className="whitespace-nowrap tabular">{formatDateTime(row.updatedAt)}</span>,
      },
      {
        id: "actions",
        header: t("platform.fields.actions"),
        cell: (row) =>
          canFeatures ? (
            <span className="flex flex-wrap gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing({ rollout: row })}>
                {t("platform.rollouts.editShort")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCompleting(row)}>
                {t("platform.rollouts.complete")}
              </Button>
            </span>
          ) : null,
      },
    ],
    [t, canFeatures],
  );

  const distributionColumns = useMemo<DataTableColumn<DistributionRow>[]>(
    () => [
      {
        id: "channel",
        header: t("platform.fields.channel"),
        cell: (row) => AGENT_UPDATE_CHANNEL_LABELS[row.channel],
      },
      {
        id: "version",
        header: t("platform.fields.version"),
        cell: (row) => <span className="font-mono">{row.version}</span>,
      },
      { id: "platform", header: t("platform.fields.platform"), cell: (row) => `${row.os}/${row.arch}` },
      {
        id: "agents",
        header: t("platform.fields.agents"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.agents)}</span>,
      },
      {
        id: "online",
        header: t("platform.fields.online"),
        align: "right",
        cell: (row) => <span className="tabular">{formatCount(row.online)}</span>,
      },
    ],
    [t],
  );

  const releaseColumns = useMemo<DataTableColumn<AgentRelease>[]>(
    () => [
      {
        id: "version",
        header: t("platform.fields.version"),
        cell: (row) => <span className="font-mono font-medium">{row.version}</span>,
      },
      {
        id: "channel",
        header: t("platform.fields.channel"),
        cell: (row) => AGENT_UPDATE_CHANNEL_LABELS[row.channel],
      },
      { id: "platform", header: t("platform.fields.platform"), cell: (row) => `${row.os}/${row.arch}` },
      {
        id: "released",
        header: t("platform.fields.releasedAt"),
        cell: (row) => <span className="tabular">{formatDate(row.releasedAt)}</span>,
      },
      {
        id: "state",
        header: t("platform.fields.status"),
        cell: (row) =>
          row.isActive ? (
            <StatusBadge tone="success">{t("platform.releases.active")}</StatusBadge>
          ) : (
            <StatusBadge>{t("platform.inactive")}</StatusBadge>
          ),
      },
      {
        id: "rollout",
        header: t("platform.fields.rolloutPercent"),
        align: "right",
        cell: (row) => <span className="tabular">%{row.rolloutPercent}</span>,
      },
      {
        id: "actions",
        header: t("platform.fields.actions"),
        cell: (row) =>
          canReleases && row.isActive ? (
            <Button size="sm" variant="ghost" onClick={() => setReleaseEditing(row)}>
              {t("platform.releases.changeRollout")}
            </Button>
          ) : null,
      },
    ],
    [t, canReleases],
  );

  return (
    <PlatformPage
      title={t("platform.releases.title")}
      description={t("platform.releases.description")}
      permission="platform.catalog.read"
    >
      <Section
        title={t("platform.rollouts.title")}
        actions={
          canFeatures ? (
            <Button size="sm" onClick={() => setEditing({ rollout: null })}>
              <Plus aria-hidden="true" />
              {t("platform.rollouts.new")}
            </Button>
          ) : null
        }
      >
        <p className="text-sm text-muted-foreground">{t("platform.rollouts.description")}</p>
        <DataTable
          caption={t("platform.rollouts.title")}
          columns={rolloutColumns}
          rows={rollouts.data ?? []}
          rowKey={(row) => row.featureKey}
          loading={rollouts.isPending}
          error={rollouts.error ? errorMessage(rollouts.error) : undefined}
          emptyState={<EmptyState icon={<Rocket />} title={t("platform.rollouts.empty")} />}
        />
      </Section>

      <Section title={t("platform.releases.distribution")}>
        {distribution.error ? (
          <Alert tone="danger">{errorMessage(distribution.error)}</Alert>
        ) : distribution.isPending ? (
          <SkeletonRows rows={3} />
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {t("platform.releases.totals", {
                agents: distribution.data.totals.agents,
                online: distribution.data.totals.online,
                beta: distribution.data.totals.beta,
              })}
            </p>
            <DataTable
              caption={t("platform.releases.distribution")}
              columns={distributionColumns}
              rows={distribution.data.items}
              rowKey={(row) => `${row.channel}-${row.version}-${row.os}-${row.arch}`}
              emptyState={<EmptyState title={t("platform.releases.noAgents")} />}
            />
            {distribution.data.tenants.length > 0 ? (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium">{t("platform.releases.byTenant")}</summary>
                <ul className="mt-2 grid gap-1">
                  {distribution.data.tenants.map((row) => (
                    <li key={row.tenant.id} className="flex flex-wrap justify-between gap-2">
                      <span>{row.tenant.name}</span>
                      <span className="text-muted-foreground">
                        {t("platform.releases.tenantLine", { agents: row.agents, beta: row.beta })} ·{" "}
                        <span className="font-mono">{row.versions.join(", ")}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        )}
      </Section>

      <Section title={t("platform.releases.agentReleases")}>
        <DataTable
          caption={t("platform.releases.agentReleases")}
          columns={releaseColumns}
          rows={releases.data ?? []}
          rowKey={(row) => row.id}
          loading={releases.isPending}
          error={releases.error ? errorMessage(releases.error) : undefined}
          emptyState={<EmptyState title={t("platform.releases.empty")} />}
        />
      </Section>

      {editing ? (
        <RolloutDialog open onOpenChange={(open) => !open && setEditing(null)} rollout={editing.rollout} />
      ) : null}
      {releaseEditing ? (
        <ReleaseRolloutDialog
          open
          onOpenChange={(open) => !open && setReleaseEditing(null)}
          release={releaseEditing}
        />
      ) : null}
      {completing ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setCompleting(null)}
          title={t("platform.rollouts.completeTitle", { feature: completing.label })}
          description={t("platform.rollouts.completeDescription")}
          confirmLabel={t("platform.rollouts.complete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() =>
            complete.mutateAsync(completing.featureKey).then(
              () => undefined,
              () => undefined,
            )
          }
        />
      ) : null}
    </PlatformPage>
  );
}
